/**
 * The one-shot `.ics` download: the same season the feed publishes, built client-side from the
 * bootstrap data already in hand. No network, so it works at the courts — and the same shared
 * renderer, so the file and the feed cannot disagree about who is on court.
 *
 * The string is built here and delivered separately, because building it is pure and delivering
 * it is three device-dependent guesses.
 */
import { CALENDAR_TIME_ZONE, renderSeasonCalendar } from '@topspin/shared';
import type { GroupBootstrap } from '@topspin/shared';
import type { SeasonView } from './data.js';

/** `topspin-2026-27.ics` — the season, so two downloads a year apart don't collide. */
export function calendarFileName(seasonId: string): string {
  return `topspin-${seasonId}.ics`;
}

/**
 * No `refreshInterval`: a downloaded file has nothing to refresh *from*, and advertising one
 * would be a promise it can't keep. The clock is the real one — this snapshot is honest about
 * when it was taken, and unlike the feed it has no `ETag` to keep stable.
 */
export function buildSeasonIcs(
  view: SeasonView,
  playerId: string,
  now: Date = new Date(),
  timeZone: string = CALENDAR_TIME_ZONE,
): string {
  const groups: GroupBootstrap[] = view.groups.map((group) => ({
    config: group.config,
    sessions: group.sessions,
    overrides: group.overrides,
    availabilities: group.availabilities,
    fills: group.fills,
  }));
  return renderSeasonCalendar(groups, { playerId, now, timeZone });
}

/**
 * Hand the file to the device. iOS is the fiddly one: a `<a download>` on a blob does nothing
 * useful in a home-screen PWA, where the share sheet's **Add All to Calendar** is the path that
 * actually imports. Everywhere else the anchor is the one that works, so try the share sheet
 * where the browser says it will take the file and fall back otherwise.
 *
 * Returns false when neither path was available, so the caller can say so rather than leaving a
 * tapped button looking broken.
 */
export async function deliverIcs(ics: string, fileName: string): Promise<boolean> {
  const file = new File([ics], fileName, { type: 'text/calendar' });
  const share = navigator.share?.bind(navigator);
  if (share && navigator.canShare?.({ files: [file] })) {
    try {
      await share({ files: [file] });
      return true;
    } catch (err) {
      // A dismissed share sheet is the player changing their mind, not a failure to report.
      if (err instanceof DOMException && err.name === 'AbortError') return true;
      // Anything else — a browser that advertises canShare and then refuses — falls through
      // to the anchor rather than leaving them with nothing.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    return true;
  } finally {
    // Revoked on the next tick: Safari needs the URL to survive the click it just handled.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
